import type { PieceDetail, PieceReport } from "@/types/piece";
import { markdownToHtml } from "@/lib/markdown";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRole } from "@/lib/useRole";
import { useApi } from "@/lib/api";

interface Props {
  piece: PieceDetail;
  reports: PieceReport[];
  /**
   * FEAT-087 — in the side column the SEO report itself is redundant: it is
   * a `seo_report` row, so it already appears under Checks & reports in the
   * trail and opens in the main window like every other document. The
   * section keeps the scores and the editable fields.
   */
  hideReport?: boolean;
}

interface SeoUpdateBody {
  seo_score?: number | null;
  aeo_score?: number | null;
  geo_score?: number | null;
  focus_keyword?: string | null;
  seo_title?: string | null;
  meta_description?: string | null;
}

const SEO_TITLE_MAX = 70;
const META_DESCRIPTION_MAX = 200;

export function SeoPanel({ piece, reports, hideReport = false }: Props) {
  // FEAT-108 / S-438 — a Reviewer reads the scores; only Admin and Editor edit.
  const { canEdit } = useRole();
  // The pipeline writes the SEO Optimizer's output as `seo_report` in
  // piece_reports. Pick the highest-version row if multiple exist.
  const seoReport = [...reports]
    .filter((r) => r.page_type === "seo_report")
    .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];

  const seoReportHtml = useMemo(
    () => markdownToHtml(seoReport?.content),
    [seoReport?.content]
  );

  // BUG-045 — SEO Optimizer computes these but historically never persisted
  // them; this is the manual fallback (and the general override path) for
  // whatever the automated extraction missed or got wrong. Always-editable
  // controlled inputs, not a separate edit-mode toggle — RightRail keys this
  // component by piece.id, so navigating to a different piece remounts it
  // with fresh initial state instead of carrying over stale edits.
  const [seoScore, setSeoScore] = useState<string>(piece.seo_score?.toString() ?? "");
  const [aeoScore, setAeoScore] = useState<string>(piece.aeo_score?.toString() ?? "");
  const [geoScore, setGeoScore] = useState<string>(piece.geo_score?.toString() ?? "");
  const [focusKeyword, setFocusKeyword] = useState(piece.focus_keyword ?? "");
  const [seoTitle, setSeoTitle] = useState(piece.seo_title ?? "");
  const [metaDescription, setMetaDescription] = useState(piece.meta_description ?? "");

  const api = useApi();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: SeoUpdateBody) =>
      api(`/pieces/${piece.id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["piece", piece.id] });
    },
  });

  function parseScoreInput(v: string): number | null | undefined {
    if (v.trim() === "") return null;
    const n = Number(v);
    if (!Number.isInteger(n) || n < 0 || n > 100) return undefined;
    return n;
  }

  const seoScoreParsed = parseScoreInput(seoScore);
  const aeoScoreParsed = parseScoreInput(aeoScore);
  const geoScoreParsed = parseScoreInput(geoScore);
  const scoreError =
    seoScoreParsed === undefined || aeoScoreParsed === undefined || geoScoreParsed === undefined;

  const dirty =
    seoScoreParsed !== (piece.seo_score ?? null) ||
    aeoScoreParsed !== (piece.aeo_score ?? null) ||
    geoScoreParsed !== (piece.geo_score ?? null) ||
    focusKeyword.trim() !== (piece.focus_keyword ?? "") ||
    seoTitle.trim() !== (piece.seo_title ?? "") ||
    metaDescription.trim() !== (piece.meta_description ?? "");

  const canSubmit = dirty && !scoreError && !mutation.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const body: SeoUpdateBody = {};
    if (seoScoreParsed !== (piece.seo_score ?? null)) body.seo_score = seoScoreParsed;
    if (aeoScoreParsed !== (piece.aeo_score ?? null)) body.aeo_score = aeoScoreParsed;
    if (geoScoreParsed !== (piece.geo_score ?? null)) body.geo_score = geoScoreParsed;
    if (focusKeyword.trim() !== (piece.focus_keyword ?? "")) {
      body.focus_keyword = focusKeyword.trim() === "" ? null : focusKeyword.trim();
    }
    if (seoTitle.trim() !== (piece.seo_title ?? "")) {
      body.seo_title = seoTitle.trim() === "" ? null : seoTitle.trim();
    }
    if (metaDescription.trim() !== (piece.meta_description ?? "")) {
      body.meta_description = metaDescription.trim() === "" ? null : metaDescription.trim();
    }
    mutation.mutate(body);
  }

  return (
    <div className="panel-body">
      <div className="panel-header" hidden={hideReport}>
        <h3>SEO &amp; Scores</h3>
        <span className="panel-sub">AEO · GEO · SEO breakdown</span>
      </div>

      <form className="settings-form" onSubmit={submit} style={{ gap: 14 }}>
        <fieldset disabled={!canEdit} style={{ display: "contents" }}>
        <div className="score-grid">
          <ScoreTile label="Quality" value={piece.quality_score} max={100} />
          <ScoreInput label="SEO" value={seoScore} onChange={setSeoScore} />
          <ScoreInput label="AEO" value={aeoScore} onChange={setAeoScore} />
          <ScoreInput label="GEO" value={geoScore} onChange={setGeoScore} />
        </div>
        {scoreError && (
          <span className="settings-field-error">Scores must be a whole number 0-100.</span>
        )}

        <label className="settings-field">
          <span className="settings-label">Focus keyword</span>
          <input
            className="settings-input"
            type="text"
            value={focusKeyword}
            maxLength={200}
            onChange={(e) => setFocusKeyword(e.target.value)}
            placeholder="Not set"
          />
        </label>

        <label className="settings-field">
          <span className="settings-label">
            SEO title
            <span className="settings-label-hint">{seoTitle.length}/{SEO_TITLE_MAX}</span>
          </span>
          <input
            className="settings-input"
            type="text"
            value={seoTitle}
            maxLength={SEO_TITLE_MAX}
            onChange={(e) => setSeoTitle(e.target.value)}
            placeholder="Not set"
          />
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Meta description
            <span className="settings-label-hint">{metaDescription.length}/{META_DESCRIPTION_MAX}</span>
          </span>
          <textarea
            className="settings-input"
            value={metaDescription}
            maxLength={META_DESCRIPTION_MAX}
            onChange={(e) => setMetaDescription(e.target.value)}
            placeholder="Not set"
            rows={3}
            style={{ height: "auto", padding: "10px 12px", resize: "vertical", fontFamily: "inherit" }}
          />
        </label>

        {mutation.isError && (
          <div className="settings-form-error">
            {mutation.error instanceof Error ? mutation.error.message : "Failed to save."}
          </div>
        )}

        <div className="settings-form-actions">
          {mutation.isSuccess && !dirty && (
            <span className="settings-label-hint">Saved</span>
          )}
          {canEdit && (
            <button type="submit" className="btn-primary" disabled={!canSubmit}>
              {mutation.isPending ? "Saving…" : "Save"}
            </button>
          )}
        </div>
        </fieldset>
      </form>

      {hideReport ? null : seoReport ? (
        <div style={{ marginTop: 16 }}>
          <div className="panel-section-label">SEO Report</div>
          <div
            className="article-body-md panel-md"
            dangerouslySetInnerHTML={{ __html: seoReportHtml }}
          />
        </div>
      ) : (
        <div className="panel-empty" style={{ marginTop: 16 }}>
          SEO report not generated yet.
        </div>
      )}
    </div>
  );
}

function ScoreTile({
  label,
  value,
  max,
}: {
  label: string;
  value: number | null;
  max: number;
}) {
  const v = value ?? 0;
  const pct = Math.max(0, Math.min(100, (v / max) * 100));
  const tone = v >= 85 ? "ok" : v >= 70 ? "info" : v > 0 ? "warn" : "muted";
  return (
    <div className={`score-tile score-tone-${tone}`}>
      <div className="score-tile-label">{label}</div>
      <div className="score-tile-value">{value == null ? "—" : v}</div>
      <div className="score-tile-bar">
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ScoreInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const n = value.trim() === "" ? null : Number(value);
  const valid = n === null || (Number.isInteger(n) && n >= 0 && n <= 100);
  const tone = !valid ? "warn" : n == null ? "muted" : n >= 85 ? "ok" : n >= 70 ? "info" : "warn";
  return (
    <div className={`score-tile score-tone-${tone}`}>
      <div className="score-tile-label">{label}</div>
      <input
        className="score-tile-input"
        type="number"
        inputMode="numeric"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="—"
      />
    </div>
  );
}
